import { useMemo, useState } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Box, Button, IconButton, MenuItem, Stack, TextField, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUpOutlined';
import { useAuth } from '../../context/authContext';
import { deleteTarget, listTargets, setTarget } from '../../api/targets';
import { CounselorTarget } from '../../types/target';
import { TableToolbar } from '../../components/table/TableToolbar';
import { PageHeader } from '../../components/PageHeader';
import { TargetFormDialog, TargetFormValues } from './TargetFormDialog';
import { TargetProgressDialog } from './TargetProgressDialog';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const now = new Date();

export const TargetsPage = () => {
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const canManage = hasPermission('targets', 'manage');

  const [periodMonth, setPeriodMonth] = useState(now.getMonth() + 1);
  const [periodYear, setPeriodYear] = useState(now.getFullYear());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [progressTargetId, setProgressTargetId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const targetsQuery = useQuery({
    queryKey: ['targets', periodMonth, periodYear],
    queryFn: () => listTargets({ periodMonth, periodYear }),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['targets'] });

  const setMutation = useMutation({
    mutationFn: (values: TargetFormValues) =>
      setTarget({
        userId: Number(values.userId),
        periodMonth: Number(values.periodMonth),
        periodYear: Number(values.periodYear),
        leadTarget: values.leadTarget === '' ? undefined : Number(values.leadTarget),
        registrationTarget: values.registrationTarget === '' ? undefined : Number(values.registrationTarget),
        admissionTarget: values.admissionTarget === '' ? undefined : Number(values.admissionTarget),
        paymentTarget: values.paymentTarget === '' ? undefined : Number(values.paymentTarget),
        notes: values.notes || undefined,
      }),
    onSuccess: () => {
      invalidate();
      setDialogOpen(false);
      setError(null);
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  const deleteMutation = useMutation({ mutationFn: deleteTarget, onSuccess: invalidate });

  const columns: GridColDef<CounselorTarget>[] = useMemo(
    () => [
      {
        field: 'user',
        headerName: 'Counselor',
        flex: 1,
        valueGetter: (_v, row) => `${row.user.firstName} ${row.user.lastName}`,
      },
      { field: 'leadTarget', headerName: 'Leads', width: 90, valueGetter: (_v, row) => row.leadTarget ?? '—' },
      {
        field: 'registrationTarget',
        headerName: 'Registrations',
        width: 130,
        valueGetter: (_v, row) => row.registrationTarget ?? '—',
      },
      {
        field: 'admissionTarget',
        headerName: 'Admissions',
        width: 110,
        valueGetter: (_v, row) => row.admissionTarget ?? '—',
      },
      {
        field: 'paymentTarget',
        headerName: 'Payment target',
        width: 130,
        valueGetter: (_v, row) => (row.paymentTarget ? Number(row.paymentTarget).toFixed(2) : '—'),
      },
      { field: 'notes', headerName: 'Notes', flex: 1 },
      {
        field: 'actions',
        headerName: '',
        width: canManage ? 100 : 60,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Stack direction="row">
            <Tooltip title="View progress">
              <IconButton size="small" onClick={() => setProgressTargetId(params.row.id)}>
                <TrendingUpIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {canManage && (
              <Tooltip title="Delete">
                <IconButton size="small" onClick={() => deleteMutation.mutate(params.row.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ),
      },
    ],
    [canManage, deleteMutation],
  );

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={TrendingUpIcon}
        color="#2F6FED"
        title="Counselor Targets"
        subtitle="Set and track monthly counselor performance targets."
        actions={
          canManage && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setError(null);
                setDialogOpen(true);
              }}
            >
              Set target
            </Button>
          )
        }
      />

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          select
          size="small"
          label="Month"
          sx={{ width: 160 }}
          value={periodMonth}
          onChange={(e) => setPeriodMonth(Number(e.target.value))}
        >
          {MONTHS.map((label, index) => (
            <MenuItem key={label} value={index + 1}>
              {label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="Year"
          type="number"
          sx={{ width: 120 }}
          value={periodYear}
          onChange={(e) => setPeriodYear(Number(e.target.value))}
        />
      </Stack>

      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1 }}>
        <DataGrid
          rows={targetsQuery.data ?? []}
          columns={columns}
          loading={targetsQuery.isLoading}
          slots={{ toolbar: TableToolbar }}
          disableRowSelectionOnClick
        />
      </Box>

      <TargetFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        errorMessage={error}
        defaultPeriodMonth={periodMonth}
        defaultPeriodYear={periodYear}
        onSubmit={async (values) => {
          await setMutation.mutateAsync(values);
        }}
      />

      <TargetProgressDialog targetId={progressTargetId} onClose={() => setProgressTargetId(null)} />
    </Box>
  );
};

const extractErrorMessage = (err: unknown): string => {
  if (isAxiosError(err)) {
    return err.response?.data?.message ?? 'Something went wrong.';
  }
  return 'Something went wrong.';
};
